import React, { useEffect, useState } from "react";
import { getMonthlyReports, lockFinance } from "../../services/ManagementService";
import { useAppContext } from "../../share-components/AppContext";
import { useNavigate } from "react-router-dom";
import FormTitle from "../../share-components/FormTitle";
import SearchColumnSelect from "../../share-components/SearchColumnSelect";
import CustomTable from "../../share-components/CustomTable";
import { customStorage, getAvailableMonths, getChangingSelectColor, getInitialAndCurrentYearMonth, getNextYearMonth, getPayloadData, 
    handleFromMonthChange, handleFromYearChange, handleToMonthChange, handleToYearChange, isNullOrEmptyString, years } from "../../services/UtilityService";
import { SERVER_ERROR_MESSAGE, TOKEN_SESSION_KEY } from "../../constants/commonConstants";
import ConfirmDialog from "../../share-components/ConfirmDialog";
import AlertDialog from "../../share-components/AlertDialog";
import { Button, Card } from "react-bootstrap";
import { getAllActiveUsers } from "../../services/CommonService";
import NoDataFound from "../../share-components/NoDataFound";

const LockedMonthlyReport = () => {
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [reportList, setReportList] = useState(null);
    const [filteredData, setFilteredData] = useState([]);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const [lastLockedYear, setLastLockedYear] = useState(null);
    const [lastLockedMonth, setLastLockedMonth] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const navigate = useNavigate();

    const token = customStorage.getItem(TOKEN_SESSION_KEY);
    const currentUser = getPayloadData(token);

    const yearMonthInfo = getInitialAndCurrentYearMonth(2, true);
    const initialYear = yearMonthInfo?.fromYear;
    const initialMonth = yearMonthInfo?.fromMonth;

    const lastMonthOfYear = yearMonthInfo?.toYear;
    const lastMonthMonth = yearMonthInfo?.toMonth;

    const [fromYear, setFromYear] = useState(initialYear);
    const [fromMonth, setFromMonth] = useState(initialMonth);
    const [toYear, setToYear] = useState(lastMonthOfYear);
    const [toMonth, setToMonth] = useState(lastMonthMonth);
    const [userById, setUserById] = useState(currentUser?.userId || "");
    const [afterGetDataStore, setAfterGetDataStore] = useState({
        fromYear: initialYear,
        fromMonth: initialMonth,
        toYear: lastMonthOfYear,
        toMonth: lastMonthMonth,
        userById: currentUser?.userId || ""
    });

    const [activeUsers, setActiveUsers] = useState([]);

    const fetchActiveUsers = async () => {
        setLoading(true);
        try {
            const response = await getAllActiveUsers();
            const sortedUsers = (response || [])?.sort((a, b) => (a.username || "")?.localeCompare((b?.username || "")));
            setActiveUsers(sortedUsers);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }


    const fetchMonthlyReports = async () => {
        try {
            setLoading(true);
            const formData = {
                userById: userById || null,
                fromYear: fromYear,
                fromMonth: fromMonth,
                toYear: toYear,
                toMonth: toMonth
            }
            const response = await getMonthlyReports(formData);
            const reports = (response?.lockedMonthlyReports || []);
            setLastLockedYear(response?.lastLockedYear || "");
            setLastLockedMonth(response?.lastLockedMonth || "");
            setReportList(reports);
            const afterFetchStore = {
                fromYear,
                fromMonth,
                toYear,
                toMonth,
                userById
            }
            setAfterGetDataStore(afterFetchStore);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchInitialLoad = async () => {
        await fetchActiveUsers();
        await fetchMonthlyReports();
    }

    useEffect(() => {
        setRouteNaviData("MANAGEMENT >> LOCKED REPORTS")
        fetchInitialLoad();
    }, []);

    const handleLockFinance = async () => {
        setLoading(true);
        try {
            await lockFinance();
            setShowSuccessDialog(true);
            setShowConfirmDialog(false);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = async () => {
        setToMonth(lastMonthMonth);
        await fetchMonthlyReports();
        setShowSuccessDialog(false);
    }

    const columns = [
        {
            Header: 'Year',
            accessor: 'targetYear',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Month',
            accessor: 'targetMonth',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Username',
            accessor: 'username',
            style: {
                maxWidth: "200px",
                fontWeight: "bold"
            }
        },
        {
            Header: 'Purchased Price(¥)',
            accessor: 'actualPurchasedAmount',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Payment Received(¥)',
            accessor: 'paymentReceivedAmount',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Service Charge(¥)',
            accessor: 'serviceChargeAmount',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Tip Money(¥)',
            accessor: 'tipMoneyAmount',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'Delivery Fee(¥)',
            accessor: 'deliExpendAmount',
            style: {
                maxWidth: "200px"
            }
        },
        {
            Header: 'General Expend(¥)',
            accessor: 'generalExpendAmount',
            style: {
                maxWidth: "200px"
            }
        }
    ];

    const getLockDate = () => {
        return !isNullOrEmptyString(lastLockedYear) && !isNullOrEmptyString(lastLockedMonth) ?
            `(${getNextYearMonth(lastLockedYear, lastLockedMonth)?.year}/${getNextYearMonth(lastLockedYear, lastLockedMonth)?.month}) ～ ` +
            `(${lastMonthOfYear}/${lastMonthMonth})`
            : `(App Started Date) ～ (${lastMonthOfYear}/${lastMonthMonth})`;
    }

    return (
        <div id='lockedMonthlyReportsPage'>
            <FormTitle title={"Locked Reports"} isCenter={true} />
            {isFirstLoaded && <>
                <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {afterGetDataStore?.fromYear}/{afterGetDataStore?.fromMonth} ～ {afterGetDataStore?.toYear}/{afterGetDataStore?.toMonth}
                </div>
                <hr style={{ margin: '5px' }} />
                <div className="text-center fw-bold mb-2">
                    {!isNullOrEmptyString(lastLockedYear) && !isNullOrEmptyString(lastLockedMonth) ? <div>Last Locked Date: ({lastLockedYear}/{lastLockedMonth})</div> :
                        <div>No Locked Finance Data!</div>}
                    {(lastLockedYear !== lastMonthOfYear || lastLockedMonth !== lastMonthMonth) &&
                        <div><Button type="button" variant="dark" className="appBtn"
                            onClick={() => setShowConfirmDialog(true)}>Click To Lock {getLockDate()}</Button></div>
                    }
                </div>
                <div style={{ overflow: "auto", paddingBottom: "50px" }}>
                    <div style={{ minWidth: "max-content", maxWidth: "850px", margin: '0 auto' }}>
                        <Card>
                            <Card.Body style={{
                                padding: '10px 5px 20px 5px'
                            }}>
                                <div>
                                    <div>
                                        <div className="mt-1 mb-1" style={{ display: "flex", gap: "10px", justifyContent: "start", alignItems: "end", flexWrap: "wrap" }}>
                                            {/* From Year & Month */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="fromYear" style={{ minWidth: "50px" }}>
                                                    From
                                                </label>
                                                <div>
                                                    <select
                                                        id="fromYear"
                                                        className={`customInputControl ${getChangingSelectColor(fromYear, afterGetDataStore?.fromYear)}`}
                                                        value={fromYear}
                                                        onChange={(e) => handleFromYearChange(e, fromMonth, toYear, toMonth, setFromYear, setFromMonth, setToYear, setToMonth)}
                                                    >
                                                        {years.map((year) => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                    <span className="separator">/</span>
                                                    <select
                                                        id="fromMonth"
                                                        className={`customInputControl ${getChangingSelectColor(fromMonth, afterGetDataStore?.fromMonth)}`}
                                                        value={fromMonth}
                                                        onChange={(e) => handleFromMonthChange(e, fromYear, toYear, toMonth, setFromMonth, setToMonth)}
                                                    >
                                                        {getAvailableMonths(fromYear).map((month) => (
                                                            <option key={month} value={month}>
                                                                {month.toString().padStart(2, "0")}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            {/* To Year & Month */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="toYear" style={{ minWidth: "50px" }}>
                                                    To
                                                </label>
                                                <div>
                                                    <select
                                                        id="toYear"
                                                        className={`customInputControl ${getChangingSelectColor(toYear, afterGetDataStore?.toYear)}`}
                                                        value={toYear}
                                                        onChange={(e) => handleToYearChange(e, fromYear, fromMonth, toMonth, setFromYear, setToYear, setFromMonth)}
                                                    >
                                                        {years.map((year) => (
                                                            <option key={year} value={year}>{year}</option>
                                                        ))}
                                                    </select>
                                                    <span className="separator">/</span>
                                                    <select
                                                        id="toMonth"
                                                        className={`customInputControl ${getChangingSelectColor(toMonth, afterGetDataStore?.toMonth)}`}
                                                        value={toMonth}
                                                        onChange={(e) => handleToMonthChange(e, fromYear, fromMonth, toYear, setFromMonth, setToMonth)}
                                                    >
                                                        {getAvailableMonths(toYear).map((month) => (
                                                            <option key={month} value={month}>
                                                                {month.toString().padStart(2, "0")}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ marginBottom: '5px', marginTop: '10px', display: 'flex', justifyContent: 'start', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                                            {/* Purchased By */}
                                            <div className="d-inline">
                                                <label className="me-1 custom-label" htmlFor="userBy" style={{ minWidth: "50px" }}>
                                                    Admin By
                                                </label>
                                                <div>
                                                    <select
                                                        id="userBy"
                                                        className={`customInputControl select-min-max-width ${getChangingSelectColor(userById, afterGetDataStore?.userById)}`}
                                                        value={userById}
                                                        onChange={(e) => setUserById(e.target.value || '')}
                                                    >
                                                        <option value="">--All--</option>
                                                        {activeUsers.map(user => (
                                                            <option key={user.userId} value={user.userId}>{user.username}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-inline">
                                            <button type='button' className="btn btn-success btn-sm appBtn"
                                                onClick={fetchMonthlyReports}
                                            >Get Data</button>
                                        </div>
                                        <div style={{ width: "max-content" }}>
                                            <div style={{ marginBottom: '10px', marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'end', flexWrap: 'wrap', gap: '5px' }}>
                                                <div style={{ display: 'inline-block' }}>
                                                    <SearchColumnSelect data={reportList} setData={setFilteredData} columns={columns} />
                                                </div>
                                            </div>
                                            {filteredData?.length > 0 ?
                                                <CustomTable
                                                    tableId='lockedReportsTbl'
                                                    columns={columns}
                                                    tblData={filteredData || []}
                                                    showPagination={true}
                                                    // initialSortBy={[{ id: 'outOfStockQuantity', desc: true }]}
                                                    itemsPerPage={20}
                                                /> : <NoDataFound />}
                                        </div>
                                    </div>
                                </div>
                                <ConfirmDialog
                                    dialogId='confirmDialog'
                                    showDialog={showConfirmDialog}
                                    // title='Logout Dialog'
                                    body={
                                        <span className="text-center">Are you sure you want to lock the finance data for following date?<br />
                                            {getLockDate()}</span>}
                                    onConfirm={handleLockFinance}
                                    onCancel={() => setShowConfirmDialog(false)}
                                />

                                <AlertDialog
                                    dialogId={'successDialog'}
                                    showDialog={showSuccessDialog}
                                    body={'You have successfully locked the finance data.'}
                                    onConfirm={handleSuccessDialogClose}
                                />
                            </Card.Body>
                        </Card>
                    </div>
                </div>
            </>}
        </div>
    );
}

export default LockedMonthlyReport;